import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Flag, Trash2, Download } from "lucide-react";
import useFetch from "@/lib/useFetch";
import api, { downloadFile } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { PageHeader, Footer, SectionCard } from "@/components/common/Page";
import Loader from "@/components/common/Loader";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmButton from "@/components/common/ConfirmButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { timeAgo } from "@/lib/communities";

const ACTIONS = [["warn", "Warning"], ["mute", "Mute"], ["tempban", "Temporary Ban"], ["permban", "Permanent Ban"]];

export default function Moderation() {
    const { community } = useAdmin();
    const { data: players } = useFetch("/players");
    const { data: communities } = useFetch("/communities");
    const url = community === "ALL" ? "/moderation" : `/moderation?community_key=${community}`;
    const { data, reload } = useFetch(url, [community]);
    const [form, setForm] = useState({ player_email: "", action: "warn", community_key: "CODERS_PLAYGROUND", reason: "", duration: "" });
    if (!players || !communities || !data) return <Loader />;
    const upd = (k, v) => setForm({ ...form, [k]: v });
    const nameFor = (k) => communities.find((c) => c.key === k)?.name || k || "—";

    const submit = async () => {
        if (!form.player_email || !form.reason) { toast.error("Player and reason required"); return; }
        try {
            const { data: p } = await api.post("/moderation/action", form);
            toast.success(`${p.action} applied`, { description: p.player_name });
            setForm({ ...form, reason: "", duration: "" }); reload();
        } catch (e) { toast.error("Failed", { description: e.response?.data?.detail || "" }); }
    };

    return (
        <div data-testid="admin-moderation">
            <PageHeader title="Moderation" subtitle="Warnings, mutes and bans with Discord sync (when the bot has permission)." />
            <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-5">
                <SectionCard title="Take Action" testId="mod-action-form">
                    <div className="space-y-3">
                        <Select value={form.player_email} onValueChange={(v) => upd("player_email", v)}>
                            <SelectTrigger data-testid="mod-player" className="bg-background border-border"><SelectValue placeholder="Select player" /></SelectTrigger>
                            <SelectContent>{players.map((p) => <SelectItem key={p.id} value={p.email}>{p.username}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="grid grid-cols-2 gap-2">
                            <Select value={form.action} onValueChange={(v) => upd("action", v)}>
                                <SelectTrigger data-testid="mod-action" className="bg-background border-border"><SelectValue /></SelectTrigger>
                                <SelectContent>{ACTIONS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={form.community_key} onValueChange={(v) => upd("community_key", v)}>
                                <SelectTrigger className="bg-background border-border"><SelectValue /></SelectTrigger>
                                <SelectContent>{communities.map((c) => <SelectItem key={c.key} value={c.key}>{c.name}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        {(form.action === "mute" || form.action === "tempban") && (
                            <Input placeholder="Duration (e.g. 24h)" value={form.duration} onChange={(e) => upd("duration", e.target.value)} className="bg-background border-border" />
                        )}
                        <Textarea data-testid="mod-reason" placeholder="Reason" value={form.reason} onChange={(e) => upd("reason", e.target.value)} className="bg-background border-border" rows={2} />
                        <Button data-testid="mod-submit" onClick={submit} className="w-full font-heading uppercase bg-danger text-white hover:bg-danger/90">Apply Action</Button>
                    </div>
                </SectionCard>

                <div className="space-y-5">
                    <SectionCard title="Punishment History" testId="mod-history" action={
                        <div className="flex items-center gap-1.5">
                            <Button data-testid="export-moderation" size="sm" variant="outline"
                                className="border-border hover:border-hazard/50 hover:text-hazard h-8 font-heading uppercase text-xs"
                                onClick={async () => { try { await downloadFile("/moderation/export", "moderation_logs.csv"); toast.success("Exported moderation_logs.csv"); } catch { toast.error("Export failed"); } }}>
                                <Download size={13} className="mr-1" /> Export
                            </Button>
                            {(data.punishments.length > 0 || data.reports.length > 0) && (
                                <ConfirmButton testId="clear-moderation" confirmTestId="clear-mod-confirm" size="sm"
                                    onConfirm={async () => { await api.delete("/moderation"); toast.success("Moderation history cleared"); reload(); }}
                                    variant="outline" className="border-danger/40 text-danger hover:bg-danger/10 h-8 font-heading uppercase text-xs"
                                    title="Clear moderation history?" description="Export first if you need a record — this permanently deletes all punishments and reports for your communities and cannot be undone." confirmLabel="Clear History">
                                    <Trash2 size={13} className="mr-1" /> Clear
                                </ConfirmButton>
                            )}
                        </div>
                    }>
                        <div className="space-y-2">
                            {data.punishments.length === 0 && <div className="text-sm text-muted-foreground">No punishments. All quiet on the server.</div>}
                            {data.punishments.map((p) => (
                                <div key={p.id} data-testid={`punishment-${p.id}`} className="flex items-start gap-3 rounded-md border border-border bg-background/40 p-3">
                                    <ShieldAlert size={15} className="text-danger mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-heading font-semibold uppercase text-foreground">{p.action}</span>
                                            <span className="text-sm text-foreground">{p.player_name}</span>
                                            <StatusBadge status={p.discord_sync_status === "success" ? "success" : p.discord_sync_status === "failed" ? "failed" : "offline"} label={`DISCORD ${(p.discord_sync_status || "n/a").toUpperCase()}`} />
                                        </div>
                                        <p className="text-sm text-muted-foreground">{p.reason}</p>
                                        <span className="label-mono text-muted-foreground">{p.moderator} · {nameFor(p.community_key)} · {timeAgo(p.created_at)}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                    <SectionCard title="Player Reports" testId="mod-reports">
                        <div className="space-y-2">
                            {data.reports.length === 0 && <div className="text-sm text-muted-foreground">No open reports.</div>}
                            {data.reports.map((r) => (
                                <div key={r.id} className="flex items-center gap-3 rounded-md border border-border bg-background/40 p-2.5">
                                    <Flag size={14} className="text-warning" />
                                    <div className="flex-1"><span className="text-foreground">{r.reported_player}</span> <span className="text-sm text-muted-foreground">— {r.reason}</span></div>
                                    <span className="label-mono text-muted-foreground">{timeAgo(r.created_at)}</span>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                </div>
            </div>
            <Footer />
        </div>
    );
}
